/**
 * WorkflowModuleGuard - Proteção de rota do módulo Workflow
 *
 * Verifica se o tenant atual possui o módulo Workflow habilitado
 * (`tenants.workflow_enabled`). Caso contrário, registra a tentativa em
 * `security_bypass_attempts` e redireciona para /tenant.
 *
 * Segue exatamente o padrão já usado por TasksModuleGuard/CrmModuleGuard.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-29 v1.0.0 - Criação do guard (Fase 1 do módulo Workflow)
 */

import { ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { useWorkflowModule } from '@/hooks/useWorkflowModule';

const workflowLogger = createLogger('WorkflowModuleGuard');

interface WorkflowModuleGuardProps {
  /** Conteúdo renderizado quando o módulo está habilitado */
  children: ReactNode;
}

/** Registra tentativa de acesso ao módulo sem habilitação */
const logWorkflowBypassAttempt = async (
  userId: string | undefined,
  tenantId: string | undefined,
  route: string
): Promise<void> => {
  try {
    if (!userId) return;

    const { error } = await supabase.from('security_bypass_attempts').insert({
      user_id: userId,
      attempted_tenant_id: tenantId || null,
      function_name: 'WorkflowModuleGuard',
      action: 'workflow_module_access_denied',
      attempted_value: route,
      user_agent: navigator.userAgent,
      details: {
        route,
        reason: 'tenant_workflow_not_enabled',
        timestamp: new Date().toISOString(),
      },
    });

    if (error) {
      workflowLogger.error('Falha ao registrar tentativa de bypass Workflow', {
        error: error.message,
      });
    }
  } catch (err) {
    workflowLogger.error('Erro ao registrar bypass attempt Workflow', { err });
  }
};

/** Bloqueia as rotas do módulo Workflow quando o tenant não o habilitou */
export function WorkflowModuleGuard({ children }: WorkflowModuleGuardProps) {
  const location = useLocation();
  const { user, currentTenant } = useAuth();
  const { isEnabled, isLoading } = useWorkflowModule();

  // Evita registrar a mesma tentativa várias vezes
  const hasLoggedBypass = useRef(false);

  useEffect(() => {
    if (!isLoading && !isEnabled && !hasLoggedBypass.current && currentTenant) {
      hasLoggedBypass.current = true;
      logWorkflowBypassAttempt(user?.id, currentTenant?.id, location.pathname);
    }
  }, [isLoading, isEnabled, user?.id, currentTenant, location.pathname]);

  // Enquanto a flag carrega, não decide nada (evita flicker/redirect indevido)
  if (isLoading) return null;

  if (!isEnabled) {
    workflowLogger.warn('Acesso ao Workflow bloqueado: módulo não habilitado', {
      tenantId: currentTenant?.id,
      route: location.pathname,
    });
    return <Navigate to="/tenant" replace />;
  }

  return <>{children}</>;
}
